<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import LearnDocsSearch from '@/components/learn/LearnDocsSearch.vue';
import AppFooter from '@/components/AppFooter.vue';
import AppNav from '@/components/AppNav.vue';
import { t } from '@/i18n';
import { localizedPath, siteLocaleFromRouteParam } from '@/lib/site-locale';
import {
  extractLearnHeadings,
  learnChapterBySlug,
  learnChapterTitle,
  learnNavSectionLabel,
  learnNavSections,
  slugifyHeading,
  visibleLearnChapters,
} from '@/lib/learn-docs';
import '@/assets/css/learn-docs.css';

const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const learnPath = (slug?: string) =>
  localizedPath(siteLocale.value, slug && slug !== 'welcome' ? `/learn/${slug}` : '/learn');
const activeSlug = computed(() => String(route.params.slug || 'welcome'));
const mobileNavOpen = ref(false);
const chapterHeadings = ref<Array<{ text: string; id: string }>>([]);

watch(
  () => route.fullPath,
  () => {
    mobileNavOpen.value = false;
  }
);

watch(
  activeSlug,
  async (slug) => {
    const chapter = learnChapterBySlug(slug);
    if (!chapter || chapter.hidden) {
      chapterHeadings.value = [];
      return;
    }

    try {
      const mod = await chapter.load();
      chapterHeadings.value = extractLearnHeadings(mod.default);
    } catch {
      chapterHeadings.value = [];
    }
  },
  { immediate: true }
);

function navItems(slugs: string[]) {
  return slugs
    .map((slug) => visibleLearnChapters.find((chapter) => chapter.slug === slug))
    .filter((item): item is (typeof visibleLearnChapters)[number] => Boolean(item));
}

function scrollToHeading(id: string) {
  mobileNavOpen.value = false;

  const doScroll = (el: Element) => {
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const tryScroll = (attempts = 0) => {
    let el = document.getElementById(id);

    if (!el) {
      document.querySelectorAll('h2, h3').forEach((h) => {
        if (el) return;
        const hId = slugifyHeading(h.textContent?.trim() ?? '');
        if (hId === id) {
          h.id = id;
          el = h as HTMLElement;
        }
      });
    }

    if (el) {
      doScroll(el);
      return;
    }
    if (attempts < 60) {
      setTimeout(() => tryScroll(attempts + 1), 50);
    }
  };
  tryScroll();
}
</script>

<template>
  <div class="min-h-screen flex flex-col bg-white dark:bg-[#141412]">
    <AppNav />
    <div class="flex flex-1 min-h-0 flex-col lg:flex-row pt-14">
      <aside
        class="w-full shrink-0 border-b border-black/[0.04] dark:border-white/[0.06] bg-white dark:bg-[#141412] lg:w-[260px] lg:border-b-0 lg:border-r lg:sticky lg:top-14 lg:max-h-[calc(100vh-3.5rem)] lg:overflow-y-auto"
        aria-label="Learn navigation"
      >
        <div class="flex items-center justify-between p-4 lg:block lg:p-6">
          <div>
            <NuxtLink
              :to="learnPath()"
              class="font-semibold text-[15px] text-black/90 dark:text-white/90 hover:text-black dark:hover:text-white transition-colors"
            >
              {{ t('landingPages.learn.shell.brand_title') }}
            </NuxtLink>
            <p
              class="mt-0.5 text-xs text-black/40 dark:text-white/35 leading-relaxed hidden lg:block"
            >
              {{ t('landingPages.learn.shell.brand_subtitle') }}
            </p>
            <div class="mt-3 hidden lg:block">
              <LearnDocsSearch />
            </div>
          </div>
          <div class="flex items-center gap-2 lg:hidden">
            <LearnDocsSearch />
            <button
              type="button"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-black/50 dark:text-white/50 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors shrink-0"
              @click="mobileNavOpen = !mobileNavOpen"
            >
              {{
                mobileNavOpen
                  ? t('landingPages.learn.shell.mobile_menu_close')
                  : t('landingPages.learn.shell.mobile_menu_open')
              }}
              <svg
                class="w-3.5 h-3.5 transition-transform duration-200"
                :class="{ 'rotate-180': mobileNavOpen }"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        <nav class="px-3 pb-4 lg:px-4 lg:pb-6" :class="mobileNavOpen ? 'block' : 'hidden lg:block'">
          <template v-for="section in learnNavSections" :key="section.id">
            <p
              class="mt-4 first:mt-0 mb-1 px-2 text-[11px] font-medium tracking-wide text-black/35 dark:text-white/30"
            >
              {{ learnNavSectionLabel(section.id) }}
            </p>
            <ul class="space-y-px">
              <li v-for="item in navItems(section.slugs)" :key="item.slug">
                <NuxtLink
                  :to="learnPath(item.slug)"
                  class="block rounded-xl px-2.5 py-[7px] text-[13px] text-black/65 dark:text-white/65 transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.05]"
                  :class="{
                    'bg-black/[0.05] dark:bg-white/[0.07] font-medium text-black/90 dark:text-white/90':
                      activeSlug === item.slug,
                  }"
                >
                  {{ learnChapterTitle(item.slug) }}
                </NuxtLink>

                <ul
                  v-if="activeSlug === item.slug && chapterHeadings.length > 0"
                  class="mt-0.5 mb-1 ml-3 pl-2.5 border-l border-black/[0.06] dark:border-white/[0.06] space-y-px"
                >
                  <li v-for="heading in chapterHeadings" :key="heading.id">
                    <a
                      :href="`#${heading.id}`"
                      class="block py-[5px] px-2 text-[12px] leading-snug text-black/45 dark:text-white/40 rounded-lg transition-colors hover:text-black/75 dark:hover:text-white/70 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] cursor-pointer"
                      @click.prevent="scrollToHeading(heading.id)"
                    >
                      {{ heading.text }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </template>
        </nav>
      </aside>

      <main class="flex-1 min-w-0 px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
        <div class="w-full min-w-0 max-w-[48rem] mx-auto">
          <slot />
        </div>
      </main>
    </div>

    <AppFooter />
  </div>
</template>
